import { Map, SourcesSpecification } from "@iventis/mapbox-gl";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { Subscription } from "rxjs";
import { pairwise } from "rxjs/operators";
import { getGoogleMapSession } from "@iventis/google/src/google-map-background-session";
import { MapStore } from "../../../../types/store-schema";
import { GoogleMapboxAttribution } from "../../attribution/engine-mapbox-google-attribution";
import { GoogleLogoMapboxAttribution } from "../../attribution/engine-mapbox-google-logo-attribution";
import { MapboxAttribution } from "../../attribution/engine-mapbox-attribution";
import { MapBackgroundDataStore } from "../../../../data-store/basemap/map-background-data-store";
import { MapboxMapBackground } from "./mapbox-map-background";
import { DeckglMapBackground } from "./deckgl-map-background";
import { DeckglMapboxIntegration } from "../../deckgl/deckgl-mapbox-integration";

/** Handles the adding and removing of a map background */
export class MapBackground {
    public readonly id: string;

    private readonly state: MapStore;

    private readonly mapBackgroundDataStore: MapBackgroundDataStore;

    private map: Map;

    private readonly deckglMapboxIntegration: DeckglMapboxIntegration;

    private mapBackground: MapboxMapBackground | DeckglMapBackground;

    private customAttribution: MapboxAttribution[] = [];

    private onFinishedLoadingBackground: () => void;

    private onMapBackgroundError: (id: string, name: string, status: number, entity: "tile" | "tile-json") => void;

    private stateSubscription: Subscription;

    private removed = false;

    constructor(
        id: string,
        mapBackgroundDataStore: MapBackgroundDataStore,
        map: Map,
        state: MapStore,
        onFinishedLoadingBackground: () => void,
        onMapBackgroundError: (id: string, name: string, status: number, entity: "tile" | "tile-json") => void,
        deckglMapboxIntegration: DeckglMapboxIntegration,
        layerScale?: number
    ) {
        this.id = id;
        this.mapBackgroundDataStore = mapBackgroundDataStore;
        this.map = map;
        this.state = state;
        this.deckglMapboxIntegration = deckglMapboxIntegration;
        this.onFinishedLoadingBackground = onFinishedLoadingBackground;
        this.onMapBackgroundError = onMapBackgroundError;
        this.addToMap(layerScale);
        this.listenToStateChange();
    }

    private async addToMap(layerScale?: number) {
        const { sources, google, metaData } = await this.mapBackgroundDataStore.getBasemap(this.id);

        // As there is an async operation above, we need to ensure since the request has been sent and completed that the map background hasn't been removed
        if (this.removed) {
            return;
        }

        // If it is a google map background, create a user session
        if (google) {
            metaData.sessionToken = await getGoogleMapSession(metaData.apiKey);
            const googleSource = sources.google;
            if (googleSource && "tiles" in googleSource && Array.isArray(googleSource.tiles)) {
                googleSource.tiles = googleSource.tiles.map((t) => t.replace("{session}", metaData.sessionToken));
            }
        }

        // Find is a 3d sitemap is being used
        const selectedSitemaps = this.state.change.value.selectedSitemapVersionLevels.value;
        const selectedSitemap3d = selectedSitemaps.find((sitemap) => sitemap.type === SitemapVersionType.ThreeDimensional);
        const mapBackgroundType = this.getMapBackgroundType(sources);

        // If 3d sitemap is being used and the map background is satellite then use Deckgl to load the map background
        if (selectedSitemap3d && mapBackgroundType === "raster") {
            this.mapBackground = new DeckglMapBackground(this.id, this.mapBackgroundDataStore, this.map, this.deckglMapboxIntegration, this.onFinishedLoadingBackground);
        } else if (selectedSitemap3d) {
            this.mapBackground = new MapboxMapBackground(
                this.id,
                this.mapBackgroundDataStore,
                this.map,
                this.state,
                this.onFinishedLoadingBackground,
                this.onMapBackgroundError,
                layerScale,
                selectedSitemap3d.perimeter
            );
        } else {
            // If a 3d sitemap is not being used or we are using vector tiles use Mapbox
            this.mapBackground = new MapboxMapBackground(
                this.id,
                this.mapBackgroundDataStore,
                this.map,
                this.state,
                this.onFinishedLoadingBackground,
                this.onMapBackgroundError,
                layerScale
            );
        }

        // If it a Google maps background we need to show their attribution, displayed differently to normal attribution
        if (google) {
            this.customAttribution.push(new GoogleLogoMapboxAttribution(this.map, "bottom-left"));
            this.customAttribution.push(new GoogleMapboxAttribution(this.map, "bottom-left", metaData.apiKey, metaData.sessionToken));
        }
    }

    private getMapBackgroundType(sources: SourcesSpecification): "raster" | "vector" {
        const isRasterPresent = Object.values(sources).some((source) => source.type === "raster");
        return isRasterPresent ? "raster" : "vector";
    }

    /** Subscribes to the map global store listening to map background specific state changes */
    private listenToStateChange() {
        this.stateSubscription = this.state.change.pipe(pairwise()).subscribe(([previous, current]) => {
            if (!this.loaded || this.removed) {
                return;
            }

            if (current.buildings3D.value !== previous.buildings3D.value) {
                this.mapBackground.updateFillExtriusionVisibility(current.buildings3D.value);
            }

            if (current.streetNames.value !== previous.streetNames.value) {
                this.mapBackground.updateSymbolsVisibility(current.streetNames.value);
            }

            // Check if a 3d sitemap has been added or removed
            if (current.selectedSitemapVersionLevels.stamp !== previous.selectedSitemapVersionLevels.stamp) {
                const previousHad3dSitemap = previous.selectedSitemapVersionLevels.value.some((sitemap) => sitemap.type === SitemapVersionType.ThreeDimensional);
                const currentHas3dSitemap = current.selectedSitemapVersionLevels.value.some((sitemap) => sitemap.type === SitemapVersionType.ThreeDimensional);

                // If a 3d sitemap has been added or removed, then reload the map background (may need to change what is displaying it)
                if ((previousHad3dSitemap && !currentHas3dSitemap) || (currentHas3dSitemap && !previousHad3dSitemap)) {
                    this.mapBackground?.remove();
                    this.customAttribution.forEach((attribution) => {
                        attribution.destroy();
                    });

                    this.addToMap();
                }
            }
        });
    }

    public get loaded() {
        return this.mapBackground?.loaded ?? false;
    }

    public remove() {
        this.removed = true;
        this.stateSubscription.unsubscribe();
        this.mapBackground?.remove();
        this.customAttribution.forEach((attribution) => {
            attribution.destroy();
        });
    }

    public onMapDestroy() {
        this.removed = true;
        this.mapBackground?.onMapDestroy();
    }
}
